import { createRpcBackend } from "@/backend/backendProvider";
import { createHandlerContext } from "@/stores/context";

export function almHandlerAc(remote, connectionId, almSessionId, context = createHandlerContext()) {
  // "ac" is Rally. Every connection op routes through the transport-switching
  // backend: NATS (served by the rally client via `registerRallyAdapters`) when
  // the flag + a registered `alm.ac` adapter allow it, WAMP otherwise. `remote`
  // is kept only for the shared handler-factory signature; it is no longer used.
  const backend = createRpcBackend(context);
  function getTags() {
    return backend
      .almToolCall("ac", "get_tags", [connectionId])
      .then((res) => res.tags.map((tag) => ({ id: tag.tag_id, name: tag.name })));
  }

  return {
    name: "Rally",
    supportsLinks: false,
    checkStatus: () => {
      return Promise.resolve({ working: true });
    },
    getMapping: () => {
      return backend.almToolCall("ac", "get_project_mapping", [connectionId]);
    },
    setProjectMapping: (mapping) => {
      return backend.almToolCall("ac", "set_project_mapping", [connectionId, mapping]);
    },
    getProjects: () => {
      return backend.almToolCall("ac", "get_projects", [connectionId]).then((res) =>
        res.projects.map((project) => ({
          id: project.object_id,
          name: project.name,
        })),
      );
    },
    getIssueFields(ac_type, field_type) {
      return backend.almToolCall("ac", "get_fields", [connectionId, ac_type, field_type]);
    },
    getIssueTypes: () => {
      return backend
        .almToolCall("ac", "get_issue_types", [], { connection_id: connectionId })
        .then((res) =>
          res.ac_types.map((ac_type) => ({
            id: ac_type.id,
            ac_type: ac_type.element_name,
            name: ac_type.display_name,
          })),
        );
    },
    getPortfolioIssueTypes: () => {
      return backend
        .almToolCall("ac", "get_portfolio_workitem_types", [], { connection_id: connectionId })
        .then((workItemTypes) =>
          workItemTypes
            .filter(({ ordinal }) => ordinal === 0)
            .map(({ type_path, display_name }) => ({
              id: type_path,
              ac_type: type_path,
              name: display_name,
            })),
        );
    },
    getReleases: async (projectId = -1, artIds = []) => {
      if (projectId === -1 && (!artIds || artIds.length === 0)) {
        return [];
      }
      if (projectId === -1) {
        projectId = null;
      }

      const releases = await backend.almToolCall("ac", "get_releases", [connectionId], {
        project_id: projectId,
        art_ids: artIds,
      });
      return releases.map((release) => ({
        id: release.ObjectID,
        name: release.Name,
      }));
    },
    getAlmTypes: () => {
      // get_alm_types is rawResponse (PI-86): the opaque snake_case ac_* keys
      // survive untransformed, so the mapping below (incl. the wsjf closure) is
      // unchanged.
      return backend.almSyncCall("ac", "session.get_alm_types", [almSessionId]).then((almTypes) =>
        almTypes.map((almType) => ({
          id: almType.id,
          issue_type: almType.ac_type,
          project_id: almType.project_id,
          wsjf: (stepEstimate) =>
            stepEstimate && almType.ac_step_value_field_elementName
              ? almType.ac_step_value_field_elementName
              : almType.ac_wsjf_field_elementName,
          tags: almType.ac_tags || [],
          release: almType.ac_release_id || -1,
        })),
      );
    },
    getTeamBoardAlmType: (almType) => {
      return almType ? { ac_type: almType.issue_type } : {};
    },
    getBacklogBoardAlmType: (sticky, almType) => {
      return almType
        ? {
            ac_type: almType.issue_type,
            selectedField: almType.wsjf(sticky.step_estimate),
            tags: almType.tags,
            release: almType.release,
          }
        : {};
    },
    getTags,
    loadSourceSprints: (source, isCreating) => {
      if (!source.project_id) {
        return Promise.resolve();
      }
      return backend
        .almToolCall("ac", "get_iterations", [connectionId, source.project_id.toString()])
        .then((iterations) => {
          source.sprints = iterations
            .filter((iter) => !isCreating || iter.State !== "Accepted")
            .map((iter) => ({ id: iter.ObjectID, name: iter.Name }));
          source.sprints.unshift({ name: "Don't sync to Rally", id: null });
        });
    },
    setSourceSprint: async (source, sessionSource) => {
      source.sprint = (sessionSource.ac_iteration_id || sessionSource.sprint) ?? null;
    },
    createTestItem: ({ project_id, work_item_type_id, default_fields }) => {
      return backend.almToolCall("ac", "create_test_item", [
        connectionId,
        project_id,
        work_item_type_id,
        default_fields,
      ]);
    },
    getWebhooks: () => {
      return backend.almToolCall("ac", "get_webhooks", [connectionId]);
    },
    createWebhook: (name) => {
      return backend.almToolCall("ac", "create_webhook", [], {
        connection_id: connectionId,
        name,
      });
    },
    deleteWebhook: (ref) => {
      return backend.almToolCall("ac", "delete_webhook", [], {
        connection_id: connectionId,
        webhook_ref: ref,
      });
    },
    getWorkspaces: () => {
      return backend.almToolCall("ac", "get_workspaces", [], { connection_id: connectionId });
    },
    getWorkspace: () => {
      return backend.almToolCall("ac", "get_workspace", [], { connection_id: connectionId });
    },
    setWorkspace: (workspaceId) => {
      return backend.almToolCall("ac", "set_workspace", [connectionId, workspaceId]);
    },
  };
}
